import React, { useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import PersonnelTrainingStatusIndicator from 'components/personnel/PersonnelTrainingStatusIndicator';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function RolesTable({ domainRoles, isLoading, onRoleClick }) {
  const currentActor = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const buildColumnsArgs = {
    isTrainingRegisterEnabled:
      currentActor.isAllowedFeature('training_register'),
    translations: {
      headers: {
        courses: i18nTShared('courses'),
        personnel: i18nTShared('personnel'),
        role: i18nTShared('role'),
        status: i18nTShared('status'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const data = useMemo(() => {
    const roles = domainRoles?.data || [];

    return isLoading ? Array(roles.length || 25).fill({}) : roles;
  }, [domainRoles?.data, isLoading]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: { isLoading },
  });

  return (
    <table className='tw-w-full tw-table-fixed tw-border-separate tw-select-none tw-rounded-b-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
      <thead className='tw-bg-grey-050'>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                className={header.column.columnDef.meta?.classNames}
                key={header.id}
              >
                <div
                  className={classNames(
                    'tw-flex tw-h-10 tw-items-center tw-truncate tw-p-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                    header.column.columnDef.meta?.isCentered ?
                      'tw-justify-center'
                    : 'tw-justify-start',
                    header.column.getIsFirstColumn() && 'tw-pl-5',
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            className={classNames(
              'tw-group/row tw-bg-white',
              !isLoading && 'tw-cursor-pointer hover:tw-bg-grey-025',
            )}
            key={row.id}
            onClick={
              isLoading ? undefined : (
                () => onRoleClick({ roleId: row.original.id })
              )
            }
          >
            {row.getVisibleCells().map((cell) => (
              <td
                className={classNames(
                  'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                  cell.column.columnDef.meta?.classNames,
                  cell.column.getIsFirstColumn() &&
                    cell.column.columnDef.id === 'role' &&
                    'tw-pl-3',
                )}
                key={cell.id}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function buildColumns({ isTrainingRegisterEnabled, translations }) {
  return [
    isTrainingRegisterEnabled && {
      id: 'status',
      header: translations.headers.status,
      cell: ({ row, table }) => {
        if (table.options.meta.isLoading)
          return (
            <div className='tw-pl-4 tw-pr-2'>
              <DotSkeleton />
            </div>
          );

        return (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-pl-4 tw-pr-2'>
            <PersonnelTrainingStatusIndicator
              status={row.original.meta.trainingStatus}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-16.5' },
    },
    {
      id: 'role',
      header: translations.headers.role,
      cell: ({ row, table }) => {
        if (table.options.meta.isLoading) return <TextSkeleton />;

        return (
          <div className='tw-truncate tw-px-2 tw-text-m tw-font-medium tw-text-grey-900'>
            {row.original.attributes.position}
          </div>
        );
      },
    },
    isTrainingRegisterEnabled && {
      id: 'courses',
      header: translations.headers.courses,
      cell: ({ row, table }) => {
        if (table.options.meta.isLoading) return <TextSkeleton />;

        return (
          <div className='tw-px-2 tw-text-center tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {row.original.meta.courseCount}
          </div>
        );
      },
      meta: { classNames: 'tw-w-42', isCentered: true },
    },
    {
      id: 'personnel',
      header: translations.headers.personnel,
      cell: ({ row, table }) => {
        if (table.options.meta.isLoading) return <TextSkeleton />;

        return (
          <div className='tw-px-2 tw-text-center tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {row.original.meta.personnelCount}
          </div>
        );
      },
      meta: { classNames: 'tw-w-42', isCentered: true },
    },
  ].filter(Boolean);
}

RolesTable.propTypes = {
  domainRoles: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('companyRole')).isRequired,
  }),
  isLoading: PropTypes.bool.isRequired,
  onRoleClick: PropTypes.func.isRequired,
};
